import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Answer, Skill } from '@/types/workerInfo.types';
import { colors, spacing } from '@/theme';
import { Zap, Plus, Trash2, Edit2, Check, X, Star } from 'lucide-react';

interface SkillInputProps {
  answer: Answer;
  onBlur: (value: { skillValue: Skill[] }) => void;
  disabled?: boolean;
}

export function SkillInput({ answer, onBlur, disabled }: SkillInputProps) {
  const [skills, setSkills] = useState<Skill[]>(answer.skillValue || []);
  const [isAdding, setIsAdding] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isSaved, setIsSaved] = useState(false);
  const [formData, setFormData] = useState<Partial<Skill>>({
    name: '',
    level: 'intermediate',
    yearsOfExperience: 1,
  });

  const levelLabels = {
    beginner: 'Principiante',
    intermediate: 'Intermedio',
    advanced: 'Avanzado',
    expert: 'Experto',
  };

  const levelColors = {
    beginner: '#3b82f6',
    intermediate: '#10b981',
    advanced: '#f59e0b',
    expert: '#8b5cf6',
  };

  const handleSubmit = () => {
    if (!formData.name) return;
    let newSkills: Skill[];
    if (editingIndex !== null) {
      newSkills = [...skills];
      newSkills[editingIndex] = formData as Skill;
    } else {
      newSkills = [...skills, formData as Skill];
    }

    newSkills.sort((a, b) => b.yearsOfExperience - a.yearsOfExperience);
    setSkills(newSkills);
    onBlur({ skillValue: newSkills });
    setIsSaved(true);
    setTimeout(() => setIsSaved(false), 2000);
    resetForm();
  };

  const handleDelete = (index: number) => {
    const newSkills = skills.filter((_, i) => i !== index);
    setSkills(newSkills);
    onBlur({ skillValue: newSkills });
    setIsSaved(true);
    setTimeout(() => setIsSaved(false), 2000);
  };

  const handleEdit = (index: number) => {
    setEditingIndex(index);
    setFormData(skills[index]);
    setIsAdding(true);
  };

  const resetForm = () => {
    setIsAdding(false);
    setEditingIndex(null);
    setFormData({
      name: '',
      level: 'intermediate',
      yearsOfExperience: 1,
    });
  };

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: spacing[4], gap: spacing[2] }}>
        <div>
          <label style={{ fontSize: 16, fontWeight: 600, color: colors.gray[900], display: 'flex', alignItems: 'center', gap: spacing[2], marginBottom: spacing[1] }}>
            <Zap size={16} color={colors.primary[500]} />
            {answer.question.question}
            {answer.question.isRequired && <span style={{ color: colors.error?.[500] || '#ef4444', marginLeft: 4 }}>*</span>}
          </label>
          {answer.question.description && <p style={{ fontSize: 14, color: colors.gray[600] }}>{answer.question.description}</p>}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: spacing[2] }}>
          <AnimatePresence>
            {isSaved && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                style={{ display: 'flex', alignItems: 'center', gap: spacing[1], color: colors.success?.[500] || '#10b981', fontSize: 12, fontWeight: 600 }}
              >
                <Check size={14} />
                <span>Guardado</span>
              </motion.div>
            )}
          </AnimatePresence>

          <motion.button
            whileTap={{ scale: 0.96 }}
            onClick={() => setIsAdding(true)}
            disabled={disabled}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: spacing[1],
              padding: `${spacing[2]} ${spacing[3]}`,
              backgroundColor: colors.primary[500],
              color: colors.white,
              border: 'none',
              borderRadius: 10,
              fontSize: 13,
              fontWeight: 600,
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.6 : 1,
            }}
          >
            <Plus size={14} />
            Agregar
          </motion.button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: spacing[3] }}>
        {skills.map((skill, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            style={{ padding: spacing[3], backgroundColor: colors.gray[50], borderRadius: 14, border: `1px solid ${colors.gray[200]}` }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: spacing[2], gap: spacing[1] }}>
              <div>
                <h4 style={{ fontSize: 14, fontWeight: 700, color: colors.gray[900] }}>{skill.name}</h4>
                <span
                  style={{
                    display: 'inline-block',
                    marginTop: spacing[1],
                    fontSize: 11,
                    padding: `${spacing[0]} ${spacing[2]}`,
                    backgroundColor: levelColors[skill.level] + '20',
                    color: levelColors[skill.level],
                    borderRadius: 999,
                    fontWeight: 600,
                  }}
                >
                  {levelLabels[skill.level]}
                </span>
              </div>
              <div style={{ display: 'flex', gap: spacing[1] }}>
                <button onClick={() => handleEdit(index)} style={{ padding: spacing[1], background: 'transparent', border: 'none', color: colors.gray[500], cursor: 'pointer' }}>
                  <Edit2 size={13} />
                </button>
                <button onClick={() => handleDelete(index)} style={{ padding: spacing[1], background: 'transparent', border: 'none', color: colors.error?.[500] || '#ef4444', cursor: 'pointer' }}>
                  <Trash2 size={13} />
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: spacing[1] }}>
              <Star size={11} color={colors.warning?.[500] || '#eab308'} fill={colors.warning?.[500] || '#eab308'} />
              <span style={{ fontSize: 12, color: colors.gray[600] }}>
                {skill.yearsOfExperience} {skill.yearsOfExperience === 1 ? 'ano' : 'anos'}
              </span>
            </div>
          </motion.div>
        ))}

        {skills.length === 0 && !isAdding && (
          <div style={{ gridColumn: '1 / -1', padding: spacing[6], backgroundColor: colors.gray[50], borderRadius: 14, border: `1px dashed ${colors.gray[300]}`, textAlign: 'center' }}>
            <Zap size={28} color={colors.gray[400]} style={{ marginBottom: spacing[2] }} />
            <p style={{ fontSize: 13, color: colors.gray[600] }}>No has agregado habilidades aun</p>
          </div>
        )}
      </div>

      <AnimatePresence>
        {isAdding && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            style={{
              marginTop: spacing[4],
              padding: spacing[4],
              backgroundColor: colors.gray[50],
              borderRadius: 16,
              border: `1px solid ${colors.primary[300]}`,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: spacing[4] }}>
              <h4 style={{ fontSize: 15, fontWeight: 700, color: colors.primary[700] }}>{editingIndex !== null ? 'Editar habilidad' : 'Nueva habilidad'}</h4>
              <button onClick={resetForm} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>
                <X size={16} color={colors.gray[500]} />
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: spacing[3], marginBottom: spacing[3] }}>
              <div>
                <label style={{ fontSize: 12, color: colors.gray[600], marginBottom: spacing[1], display: 'block' }}>Nombre *</label>
                <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} style={{ width: '100%', padding: spacing[2], borderRadius: 8, border: `1px solid ${colors.gray[300]}`, fontSize: 14 }} placeholder="React, Python..." />
              </div>
              <div>
                <label style={{ fontSize: 12, color: colors.gray[600], marginBottom: spacing[1], display: 'block' }}>Nivel *</label>
                <select value={formData.level} onChange={(e) => setFormData({ ...formData, level: e.target.value as Skill['level'] })} style={{ width: '100%', padding: spacing[2], borderRadius: 8, border: `1px solid ${colors.gray[300]}`, fontSize: 14, backgroundColor: colors.white }}>
                  <option value="beginner">Principiante</option>
                  <option value="intermediate">Intermedio</option>
                  <option value="advanced">Avanzado</option>
                  <option value="expert">Experto</option>
                </select>
              </div>
            </div>

            <div style={{ marginBottom: spacing[4] }}>
              <label style={{ fontSize: 12, color: colors.gray[600], marginBottom: spacing[1], display: 'block' }}>Anos de experiencia *</label>
              <input type="number" value={formData.yearsOfExperience} onChange={(e) => setFormData({ ...formData, yearsOfExperience: parseFloat(e.target.value) || 0 })} style={{ width: '100%', padding: spacing[2], borderRadius: 8, border: `1px solid ${colors.gray[300]}`, fontSize: 14 }} min={0} max={50} step={0.5} />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: spacing[2] }}>
              <button onClick={resetForm} style={{ padding: `${spacing[2]} ${spacing[3]}`, backgroundColor: 'transparent', border: `1px solid ${colors.gray[300]}`, borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>
                Cancelar
              </button>
              <button onClick={handleSubmit} style={{ padding: `${spacing[2]} ${spacing[3]}`, backgroundColor: colors.primary[500], color: colors.white, border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                {editingIndex !== null ? 'Actualizar' : 'Agregar'}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
